// Bowdoin College: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Bowdoin's Common Data Set 2025-2026: C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table, C8 test policy for
//    Fall 2027 and the score date, C21/C22 plans. Quirks: C21 labels its
//    early decision counts (1,985 applied, 296 admitted) "For the Fall 2024
//    entering class", so they are left out; G1 gives the 2025-26 charges
//    ("The 2026-27 fees will be available by June 2026"), so costs come from
//    Student Aid's 2026-2027 Cost of Attendance instead.
//  - bowdoin.edu admissions: Apply (requirements, test-optional choice, ED
//    agreement, decision release) and Application Dates and Deadlines.
//  - College Scorecard API, school id 161004 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Bowdoin Common Data Set 2025-2026',
        url: 'https://www.bowdoin.edu/ir/pdf/bowdoin-cds_2025-2026.pdf',
        read: '2026-10-01',
    },
    apply: {
        label: 'Bowdoin Admissions, Apply',
        url: 'https://www.bowdoin.edu/admissions/apply/',
        read: '2026-10-01',
    },
    dates: {
        label: 'Bowdoin Admissions, Application Dates and Deadlines',
        url: 'https://www.bowdoin.edu/admissions/apply/dates-deadlines/index.html',
        read: '2026-10-01',
    },
    coa: {
        label: 'Bowdoin Student Aid, 2026-2027 Cost of Attendance',
        url: 'https://www.bowdoin.edu/student-aid/cost-of-attendance/index.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?161004-Bowdoin-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'bowdoin-college',
    kbId: 'bowdoin_college',
    name: 'Bowdoin College',
    shortName: 'Bowdoin',
    location: 'Brunswick, ME',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Bowdoin visual identity, color and typography); decoration only.
    branding: {
        primary: '#000000',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/bowdoin_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 14045,
        admitted: 957,
        enrolled: 515,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 540, admitted: 74 },
            { group: 'Out-of-state', applied: 7464, admitted: 807 },
            { group: 'International', applied: 6041, admitted: 76 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Bowdoin\'s Common Data Set reports one first-year acceptance rate for the whole college, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'The Common Data Set gives early decision counts only for an earlier class, so this page shows no early decision acceptance rate. Bowdoin has no early action plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Decision 1', deadline: 'November 15', decision: 'Mid-December', binding: true },
            { plan: 'Early Decision 2', deadline: 'January 5', decision: 'Early February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'Mid-March', binding: false },
        ],
        note: 'The application opens August 1. Recommendations, transcripts and other materials are accepted after the deadline. The optional Arts Supplement is due November 15 for Early Decision 1 and January 13 for Early Decision 2 and Regular Decision. Bowdoin is also a QuestBridge partner; for QuestBridge Match applicants, the Arts Supplement and financial aid materials are due November 1.',
        source: 'dates',
        binding: 'Early Decision is binding: you, a parent and your school counselor sign an Early Decision Agreement, for students who believe Bowdoin is their top choice.',
        bindingSource: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Bowdoin has been test-optional since 1969: the SAT and ACT are not required. The application asks, test by test, whether you want your scores used, and you can change that choice until the deadline. Homeschooled applicants send a graded paper or lab assignment, or test scores.',
        source: 'apply',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants, with scores due by January 5.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 74268 },
            { item: 'Fees', amount: 700 },
            { item: 'Housing', amount: 10106 },
            { item: 'Food (21-meal plan)', amount: 10326 },
            { item: 'Books, course materials and supplies', amount: 840 },
            { item: 'Personal expenses', amount: 1660 },
            { item: 'Travel', amount: null },
        ],
        total: 97900,
        note: 'Travel depends on where you live, so it is not in the $97,900 total; Bowdoin writes the total as $97,900 or more.',
        source: 'coa',
        netPrice: {
            average: 15595,
            byIncome: [
                { income: '$0 to $30,000', amount: 3318 },
                { income: '$30,001 to $48,000', amount: 5634 },
                { income: '$48,001 to $75,000', amount: 8694 },
                { income: '$75,001 to $110,000', amount: 16219 },
                { income: 'Over $110,000', amount: 40929 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Bowdoin\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
